// CalendarViewCompact.jsx

import React, { useCallback } from 'react';

// Hooks
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';

// Components
import AgendaView from './views/agenda/AgendaView';

/**
 * CalendarViewCompact
 *
 * Compact calendar renderer used in constrained surfaces (e.g. dashboard cards).
 * Uses AgendaView because it reads well in both full and compact contexts.
 * 
 * @param {Object} view - The calendar view configuration.
 * @param {Object} typeDef - The type definition for the calendar nodes.
 * @param {Array} nodes - The list of calendar nodes to display.
 * @param {string} language - The language code for formatting dates and times.
 *  
 *
 * @returns {ReactNode} - CalendarViewCompact.
 */
const CalendarViewCompact = React.memo(({ view, typeDef, nodes = [], eventItems, language = 'en', onItemClick = null }) => {

  // ----- HANDLERS ----- //

  // Calendar Node Navigation

  const handleOpenNode = useDefaultNodeOpen();

  /**
   * handleNodeClick
   * 
   * Handles clicks on calendar event nodes, opening peek by default.
   * 
   * @param {Event} event - The click event.
   * @param {Object} node - The calendar event node that was clicked.
   */
  const handleNodeClick = useCallback((event, nodeOrItem) => {
    event?.preventDefault?.();
    const node = nodeOrItem?.node || nodeOrItem;
    if (typeof onItemClick === 'function') {
      event?.stopPropagation?.();
      onItemClick(node);
      return;
    }
    handleOpenNode(node);
  }, [handleOpenNode, onItemClick]);

  /**
   * handleNodeDoubleClick
   *
   * Handles double clicks on agenda events without reopening the modal path.
   *
   * @param {Event} event - The click event.
   * @param {Object} node - The calendar event node that was clicked.
   */
  const handleNodeDoubleClick = useCallback((event, nodeOrItem) => {
    event?.preventDefault?.();
    if (typeof onItemClick === 'function') {
      event?.stopPropagation?.();
      return;
    }
    const node = nodeOrItem?.node || nodeOrItem;
    handleOpenNode(node);
  }, [handleOpenNode, onItemClick]);

  // ----- RENDER ----- //

  return (
    <AgendaView
      view={view}
      typeDef={typeDef}
      nodes={nodes}
      eventItems={eventItems}
      language={language}
      compact
      onNodeClick={handleNodeClick}
      onNodeDoubleClick={handleNodeDoubleClick}
    />
  );
});

// Dev Tools
CalendarViewCompact.displayName = 'CalendarViewCompact';

export default CalendarViewCompact;
